import React from 'react';
import {
  Plus,
  RefreshCw,
  AlertTriangle,
  ChevronRight,
  Users,
  Star,
  Loader2,
} from 'lucide-react';
import { cn } from '../poker-ui/utils/cn';
import { LobbyFiskiIcon, getPlayerLeague } from './lobbyHubShared';
import { HUB_SOCKET_PHASE } from '../realtime/connectionUx';
import { LOBBY_HOME_ASSETS } from '../lib/lobbyHomeAssets';

export const BUCKS_IMG = `${process.env.PUBLIC_URL || ''}/bucks.png`;
export const belotPlayerModes = [
  { value: 4, title: '2 на 2', subtitle: 'Командный (арм./молд.)' },
  { value: 2, title: '1 на 1', subtitle: 'Открытый белот' },
];
export const sekaPlayerModes = [
  { value: 2, title: '2 места', subtitle: 'Дуэль' },
  { value: 3, title: '3 места', subtitle: 'Быстро' },
  { value: 4, title: '4 места', subtitle: 'Баланс' },
  { value: 5, title: '5 мест', subtitle: 'Турбо стол' },
  { value: 6, title: '6 мест', subtitle: 'Макс. экшен' },
];
export const pokerPlayerModes = [
  { value: 2, title: '2 игрока', subtitle: 'Heads-up · Дуэль' },
  { value: 6, title: '6 игроков', subtitle: 'Short-handed' },
  { value: 9, title: '9 игроков', subtitle: 'Full-ring' },
];

/** Дискретные значения анте (Сека) — как в OnlineLobby. */
export const SEKA_ROOM_ANTE_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 50, 75, 100, 150, 200];

export function normalizeBuyInValue(value, cfg) {
  const raw = Number(value);
  const safe = Number.isFinite(raw) ? raw : cfg.min;
  const rounded = Math.round(safe / cfg.step) * cfg.step;
  const clamped = Math.max(cfg.min, Math.min(cfg.max, rounded));
  return Number(clamped.toFixed(cfg.step < 1 ? 2 : 0));
}
export const ROOM_THEME = {
  seka: {
    label: 'Сека',
    logoSrc: '/logotip-seka-button.png',
    shell: 'from-[#3a240f] via-[#24170c] to-[#130f09]',
    ring: 'ring-amber-500/20',
    badge: 'border-amber-300/40 bg-amber-500/15 text-amber-100',
    card: 'border-amber-300/20 bg-amber-500/[0.06]',
    accentText: 'text-amber-200',
    slider: 'accent-[#d6a93c]',
    sliderFill: 'rgba(251,191,36,0.72)',
    inputFocus: 'focus:border-amber-300/60 focus:ring-amber-300/40',
    toggleOn: 'border-amber-300/45 bg-amber-500/15 text-amber-100',
    createToggleBtn: 'border-[#c9a227]/50 bg-gradient-to-b from-[#b8860b] to-[#7a5c12] text-white',
    primaryBtn: 'from-[#d0a329] to-[#8c6a14] text-[#201503]',
  },
  poker: {
    label: 'Покер',
    logoSrc: '/logotip-poker-button.png',
    shell: 'from-[#133022] via-[#10261b] to-[#0a1710]',
    ring: 'ring-emerald-500/20',
    badge: 'border-emerald-300/40 bg-emerald-500/15 text-emerald-100',
    card: 'border-emerald-300/20 bg-emerald-500/[0.06]',
    accentText: 'text-emerald-200',
    slider: 'accent-[#34d399]',
    sliderFill: 'rgba(52,211,153,0.72)',
    inputFocus: 'focus:border-emerald-300/60 focus:ring-emerald-300/40',
    toggleOn: 'border-emerald-300/45 bg-emerald-500/15 text-emerald-100',
    createToggleBtn: 'border-emerald-400/50 bg-gradient-to-b from-[#2e8f62] to-[#145338] text-white',
    primaryBtn: 'from-[#2e8f62] to-[#145338] text-[#ecfff3]',
  },
  belot: {
    label: 'Белот',
    logoSrc: '/belot-button.png',
    shell: 'from-[#25133f] via-[#1b1231] to-[#120d21]',
    ring: 'ring-violet-500/20',
    badge: 'border-violet-300/40 bg-violet-500/15 text-violet-100',
    card: 'border-violet-300/20 bg-violet-500/[0.06]',
    accentText: 'text-violet-200',
    slider: 'accent-[#a78bfa]',
    sliderFill: 'rgba(167,139,250,0.72)',
    inputFocus: 'focus:border-violet-300/60 focus:ring-violet-300/40',
    toggleOn: 'border-violet-300/45 bg-violet-500/15 text-violet-100',
    createToggleBtn: 'border-violet-400/50 bg-gradient-to-b from-[#7a5ad8] to-[#4a2a96] text-white',
    primaryBtn: 'from-[#7a5ad8] to-[#4a2a96] text-[#f6f1ff]',
  },
};

const ROOM_MODE_META = {
  seka: {
    label: 'Сека',
    logoSrc: '/logotip-seka-button.png',
    tone: 'border-white/35 bg-white/10 text-white',
  },
  poker: {
    label: 'Покер',
    logoSrc: '/logotip-poker-button.png',
    tone: 'border-white/35 bg-white/10 text-white',
  },
  belot: {
    label: 'Белот',
    logoSrc: '/belot-button.png',
    tone: 'border-white/35 bg-white/10 text-white',
  },
};

function toDisplayNickname(rawValue) {
  const raw = String(rawValue || '').trim();
  if (!raw) return 'Игрок';
  if (/^(u|dev)_[a-z0-9_-]{8,}$/i.test(raw)) return 'Игрок';
  return raw;
}

function LobbyTableSeatAvatar({ player }) {
  const un = toDisplayNickname(player?.username || player?.userId || player?.id);
  const lvl = Math.max(1, Math.floor(Number(player?.level) || 1));
  const league = getPlayerLeague(lvl);
  const avatar = String(player?.avatarUrl || '').trim();
  const initial = (un || 'И').charAt(0).toUpperCase();
  return (
    <div className="flex shrink-0 items-center gap-1" title={un}>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white/30 bg-black/45 text-sm font-black text-white shadow-[inset_0_0_12px_rgba(0,0,0,0.5)]">
        {avatar ? (
          <img src={avatar} alt="" className="h-full w-full object-cover" draggable={false} />
        ) : (
          initial
        )}
      </div>
      <span
        className="relative inline-flex h-7 w-7 shrink-0 items-center justify-center"
        aria-label={`Уровень ${lvl}`}
      >
        <Star className={`absolute inset-0 h-7 w-7 drop-shadow-md ${league.starClass}`} strokeWidth={1.15} aria-hidden />
        <span
          className="relative z-[1] font-black tabular-nums text-[8px] text-white"
          style={{ WebkitTextStroke: '1px rgb(0 0 0)', paintOrder: 'stroke fill' }}
        >
          {lvl}
        </span>
      </span>
    </div>
  );
}

/**
 * Заголовок «Столы», форма создания и список столов Секи (как в основном лобби).
 */
export function LobbyTablesBlock({
  mode = 'seka',
  user,
  tables,
  tablesLoaded,
  loading,
  refreshing,
  error,
  onOpenCreateRoom,
  onRefreshTables,
  handleJoinTable,
  handleLeaveTable,
  formatTableName,
  getStatusText,
  onJoinTable,
  /**
   * Если задан — кнопка «Играть» ведёт в онлайн-ожидание (как «Войти» в OnlineLobby), а не сразу за игровой стол.
   */
  onPlayThroughOnlineLobby,
  /** Фаза глобального Colyseus-лобби из App (не использовать только socket?.connected). */
  hubSocketPhase = HUB_SOCKET_PHASE.ONLINE,
  /** Кнопки «Обновить / Создать» из макета `lobby-home` без лишних рамок. */
  toolbarVisual = 'default',
}) {
  const theme = ROOM_THEME[mode] || ROOM_THEME.seka;
  const emptyRoomsFrameClass =
    mode === 'belot'
      ? 'bg-gradient-to-br from-violet-300/45 via-violet-600/38 to-[#3b1f6e]/55'
      : 'bg-gradient-to-br from-[#e8c547]/50 via-[#a67c2d]/40 to-[#5c4518]/55';
  const hubLobbyReady = hubSocketPhase === HUB_SOCKET_PHASE.ONLINE;
  const hubLobbyOffline = hubSocketPhase === HUB_SOCKET_PHASE.OFFLINE;
  const hubLobbyProgress =
    hubSocketPhase === HUB_SOCKET_PHASE.CONNECTING || hubSocketPhase === HUB_SOCKET_PHASE.RECONNECTING;

  const renderCurrencyIcon = (currency, sizeClass = 'h-3.5 w-3.5') => {
    if (currency === 'bucks') {
      return (
        <img
          src={BUCKS_IMG}
          alt=""
          width={18}
          height={18}
          className={cn(sizeClass, 'shrink-0 object-contain')}
          draggable={false}
          aria-hidden
        />
      );
    }
    return <LobbyFiskiIcon size="sm" className={sizeClass} />;
  };

  return (
    <>
      {hubLobbyOffline ? (
        <div
          className="mb-3 flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-950/35 px-3 py-2.5 text-xs text-amber-100"
          role="status"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" aria-hidden />
          <span>Нет соединения с лобби. Проверьте сеть и обновите список.</span>
        </div>
      ) : null}
      {hubLobbyProgress ? (
        <div
          className="mb-3 flex items-start gap-2 rounded-xl border border-cyan-500/35 bg-cyan-950/25 px-3 py-2.5 text-xs text-cyan-100"
          role="status"
        >
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-cyan-300" aria-hidden />
          <span>
            {hubSocketPhase === HUB_SOCKET_PHASE.CONNECTING
              ? 'Подключение к лобби…'
              : 'Восстановление связи с лобби…'}
          </span>
        </div>
      ) : null}
      <div
        className={cn(
          'mb-2 flex justify-between gap-2 px-1',
          toolbarVisual === 'homeArt' ? 'items-end' : 'items-center'
        )}
      >
        {toolbarVisual === 'homeArt' ? (
          <h3 className="min-w-0 shrink text-left text-2xl font-black uppercase leading-none tracking-[0.08em] text-white sm:text-[1.75rem] sm:tracking-[0.09em]">
            Столы
          </h3>
        ) : (
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#8fa3bf]">Столы</h2>
        )}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {toolbarVisual === 'homeArt' ? (
            <>
              <button
                type="button"
                onClick={onRefreshTables}
                disabled={refreshing}
                aria-label="Обновить список столов"
                title="Обновить"
                className="lobby-focus relative m-0 flex h-[52px] w-[52px] shrink-0 items-center justify-center border-0 bg-transparent p-0 shadow-none outline-none transition active:scale-[0.98] disabled:opacity-45 sm:h-[54px] sm:w-[54px]"
              >
                <span
                  className={cn(
                    'flex h-full w-full items-center justify-center',
                    refreshing && 'lobby-refresh-spin animate-spin'
                  )}
                  aria-hidden
                >
                  <img
                    src={LOBBY_HOME_ASSETS.btnRefreshRooms}
                    alt=""
                    className="pointer-events-none h-full w-full max-h-[48px] max-w-[48px] object-contain sm:max-h-[50px] sm:max-w-[50px]"
                    draggable={false}
                  />
                </span>
              </button>
              <button
                type="button"
                onClick={() => onOpenCreateRoom?.()}
                disabled={!hubLobbyReady}
                aria-label="Создать стол"
                title="Создать"
                className="lobby-focus m-0 flex h-[50px] min-w-0 max-w-[9.5rem] flex-1 items-center justify-center border-0 bg-transparent p-0 shadow-none outline-none transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 sm:h-[52px] sm:max-w-[10.5rem]"
              >
                <img
                  src={LOBBY_HOME_ASSETS.btnCreateTable}
                  alt=""
                  className="pointer-events-none h-auto w-full max-h-[48px] object-contain sm:max-h-[50px]"
                  draggable={false}
                />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onRefreshTables}
                disabled={refreshing}
                aria-label="Обновить список столов"
                className="lobby-focus flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[#b8c9e0] transition duration-150 hover:bg-white/10 hover:text-white disabled:opacity-50"
                title="Обновить"
              >
                <RefreshCw
                  className={cn('h-4 w-4 lobby-refresh-spin', refreshing && 'animate-spin')}
                  aria-hidden
                />
              </button>
              <button
                type="button"
                onClick={() => onOpenCreateRoom?.()}
                disabled={!hubLobbyReady}
                aria-label="Создать стол"
                className={cn(
                  'lobby-focus flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-bold uppercase tracking-wide shadow-md transition duration-150 ease-out active:scale-[0.98] active:opacity-90 disabled:cursor-not-allowed disabled:opacity-45',
                  theme.createToggleBtn
                )}
              >
                <Plus className="h-4 w-4" strokeWidth={2.5} aria-hidden />
                Создать
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div
          className="mb-4 flex items-start gap-2 rounded-xl border border-red-500/40 bg-red-950/40 px-3 py-2.5 text-sm text-red-200"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
          <span>{error}</span>
        </div>
      )}


      <div className="lobby-mp-scroll flex min-h-[120px] flex-col gap-3 max-sm:snap-x max-sm:snap-mandatory max-sm:overflow-x-auto max-sm:pb-2 sm:grid sm:grid-cols-2 sm:overflow-x-visible sm:pb-4 lg:grid-cols-3">
        {!tablesLoaded && tables.length === 0 ? (
          <div className="flex w-full gap-3 max-sm:flex-row sm:contents">
            {[0, 1].map((k) => (
              <div
                key={k}
                className="h-[168px] w-[min(88vw,360px)] shrink-0 snap-start animate-pulse rounded-2xl bg-white/[0.06] ring-1 ring-white/10 sm:h-[176px] sm:w-full sm:min-w-0"
                aria-hidden
              />
            ))}
          </div>
        ) : tables.length === 0 ? (
          <div className={cn('rounded-2xl p-[2px] sm:p-[2.5px]', emptyRoomsFrameClass)}>
            <div className="relative isolate h-[min(58vw,268px)] w-full min-h-[12.5rem] overflow-hidden rounded-[0.9rem] border border-white/[0.09] bg-[#070a10] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] sm:h-[15.5rem] sm:min-h-[14rem] sm:rounded-[0.95rem]">
              <img
                src={LOBBY_HOME_ASSETS.bgNoOpenRooms}
                alt=""
                className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover object-center select-none"
                draggable={false}
                aria-hidden
              />
              <div className="pointer-events-none absolute inset-0 z-[1] flex flex-col items-center justify-center gap-2 px-6 text-center">
                <p className="max-w-[16rem] text-sm font-bold uppercase tracking-wide text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.85)] sm:max-w-[18rem] sm:text-base sm:tracking-[0.06em]">
                  Нет открытых столов
                </p>
                <p className="max-w-[17rem] text-xs leading-relaxed text-white/95 [text-shadow:0_1px_3px_rgba(0,0,0,0.8)] sm:text-sm">
                  Создайте стол или сыграйте с ботом
                </p>
              </div>
            </div>
          </div>
        ) : (
          tables.map((table) => {
            const isUserInTable = table.players.some((p) => p.username === user.username);
            const canJoin =
              table.players.length < table.maxPlayers &&
              !isUserInTable &&
              table.status === 'waiting';
            const tableMode = String(table.mode || mode || 'seka').toLowerCase();
            const modeMeta = ROOM_MODE_META[tableMode] || ROOM_MODE_META.seka;
            const seatedPlayers = Array.isArray(table.players) ? table.players : [];

            return (
              <div
                key={table.id}
                className={cn(
                  'relative max-sm:w-[min(88vw,360px)] max-sm:shrink-0 max-sm:snap-start overflow-hidden rounded-2xl border border-white/15 bg-gradient-to-b p-4 sm:w-full sm:min-w-0',
                  'from-[#1c2535]/95 via-[#131b29]/95 to-[#0f141d]/95'
                )}
              >
                <div className="relative">
                <div className="mb-3 flex items-start justify-between gap-2">
                  <h3 className="min-w-0 flex-1 truncate text-base font-bold text-white sm:text-lg">
                    {formatTableName(table)}
                  </h3>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-2.5 py-1 text-[11px] font-black uppercase tracking-wide',
                      table.status === 'playing'
                        ? 'bg-amber-500/20 text-amber-200 ring-1 ring-amber-500/30'
                        : 'bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-500/30'
                    )}
                  >
                    {getStatusText(table.status)}
                  </span>
                </div>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <span
                    className={cn(
                      'inline-flex min-h-[40px] items-center gap-2 rounded-full border px-4 py-2 text-base font-black uppercase tracking-[0.06em]',
                      modeMeta.tone
                    )}
                  >
                    <img
                      src={modeMeta.logoSrc}
                      alt=""
                      className="h-8 w-auto max-w-[72px] object-contain sm:h-9"
                      draggable={false}
                      aria-hidden
                    />
                    {modeMeta.label}
                  </span>
                  <div className="flex max-w-[min(220px,52vw)] flex-wrap items-center justify-end gap-2 sm:max-w-none">
                    {seatedPlayers.length ? (
                      seatedPlayers.map((p, idx) => (
                        <LobbyTableSeatAvatar
                          key={`${table.id}-seat-${String(p?.userId || p?.id || idx)}`}
                          player={p}
                        />
                      ))
                    ) : (
                      <span className="text-sm font-semibold text-white/45">Никого</span>
                    )}
                  </div>
                </div>
                <div className="mb-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#8fa3bf]">
                  <span className="flex items-center gap-1.5 font-semibold">
                    <Users className="h-4 w-4 shrink-0" />
                    {table.players.length}/{table.maxPlayers}
                  </span>
                  <span className="flex items-center gap-1.5 font-mono text-[#e8c547]">
                    {renderCurrencyIcon((table.currency || 'seka').toLowerCase())}
                    {(table.currency || 'seka').toLowerCase() === 'bucks'
                      ? Number(table.buyIn || 0).toLocaleString('ru-RU', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : Number(table.buyIn || 0).toLocaleString('ru-RU')}
                    <span className="text-xs font-bold uppercase text-white/70">
                      {(table.currency || 'seka').toLowerCase() === 'bucks' ? '€' : 'ФИШКИ'}
                    </span>
                  </span>
                </div>
                <div className="flex gap-2">
                  {isUserInTable ? (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          onJoinTable({
                            tableId: table.id,
                            gameRoomId: table.gameRoomId || '',
                            mode: table.mode || mode,
                          })
                        }
                        aria-label={`Вернуться к столу ${formatTableName(table)}`}
                        className="lobby-focus flex min-h-[48px] flex-1 items-center justify-center gap-1 rounded-xl bg-gradient-to-b from-[#2d6a9f] to-[#1a3d5c] text-base font-bold text-white transition duration-150 active:scale-[0.98]"
                      >
                        За стол
                        <ChevronRight className="h-4 w-4" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleLeaveTable(table.id)}
                        aria-label="Покинуть стол"
                        className="lobby-focus min-h-[48px] rounded-xl border border-white/15 px-3 text-base font-semibold text-[#b8c9e0] transition duration-150 hover:bg-white/5"
                      >
                        Выйти
                      </button>
                    </>
                  ) : canJoin ? (
                    <button
                      type="button"
                      onClick={() =>
                        onPlayThroughOnlineLobby
                          ? onPlayThroughOnlineLobby(table)
                          : handleJoinTable(table)
                      }
                      disabled={loading}
                      aria-label={`Играть за столом ${formatTableName(table)}`}
                      className="lobby-focus flex min-h-[52px] w-full items-center justify-center rounded-xl bg-gradient-to-b from-[#c9a227] to-[#8a6a12] text-base font-bold text-[#1a1204] transition duration-150 ease-out active:scale-[0.98] active:opacity-90 disabled:opacity-50"
                    >
                      Играть
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled
                      aria-label={table.status === 'playing' ? 'Игра уже идёт' : 'Стол полон'}
                      className="min-h-[52px] w-full rounded-xl border border-white/10 bg-white/5 text-base font-semibold text-white/40"
                    >
                      {table.status === 'playing' ? 'Идёт игра' : 'Полон'}
                    </button>
                  )}
                </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
